import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import { AutocompleteSuggestions } from './AutocompleteSuggestions';

interface AutocompleteSuggestion {
    id: number;
    name: string;
    url: string;
    contentType: string;
}

interface SearchFormProps {
    query: string;
    placeholder: string;
    buttonText: string;
    loading: boolean;
    onQueryChange: (query: string) => void;
    onSubmit: (e: React.FormEvent) => void;
    onSuggestionSelect?: (suggestion: AutocompleteSuggestion) => void;
    autocompleteApiUrl?: string;
    debounceMs?: number;
}

export const SearchForm: React.FC<SearchFormProps> = memo(({
    query,
    placeholder,
    buttonText,
    loading,
    onQueryChange,
    onSubmit,
    onSuggestionSelect,
    autocompleteApiUrl = '/umbraco/api/SearchApi/Autocomplete',
    debounceMs = 300
}) => {
    const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<NodeJS.Timeout>();

    const fetchSuggestions = useCallback(async (searchQuery: string) => {
        if (!searchQuery.trim() || searchQuery.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        
        try {
            const response = await fetch(`${autocompleteApiUrl}?query=${encodeURIComponent(searchQuery)}&maxResults=5`);
            if (response.ok) {
                const data = await response.json();
                setSuggestions(data.suggestions || []);
                setShowSuggestions(data.suggestions?.length > 0);
                setHighlightedIndex(-1);
            }
        } catch (error) {
            console.error('Error fetching autocomplete suggestions:', error);
            setSuggestions([]);
            setShowSuggestions(false);
        }
    }, [autocompleteApiUrl]);

    const debouncedFetchSuggestions = useCallback((searchQuery: string) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        
        debounceRef.current = setTimeout(() => {
            fetchSuggestions(searchQuery);
        }, debounceMs);
    }, [fetchSuggestions, debounceMs]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        onQueryChange(newValue);
        debouncedFetchSuggestions(newValue);
    };

    const handleInputFocus = () => {
        if (query.length >= 2 && suggestions.length > 0) {
            setShowSuggestions(true);
        }
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        // Delay hiding suggestions to allow for suggestion clicks
        setTimeout(() => {
            setShowSuggestions(false);
            setHighlightedIndex(-1);
        }, 150);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showSuggestions || suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev < suggestions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev > 0 ? prev - 1 : suggestions.length - 1
                );
                break;            
            case 'Enter':
                if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
                    e.preventDefault();
                    const suggestion = suggestions[highlightedIndex];
                    handleSuggestionSelect(suggestion);
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                setHighlightedIndex(-1);
                inputRef.current?.blur();
                break;
        }
    };    
    
    const handleSuggestionClick = (suggestion: AutocompleteSuggestion) => {
        handleSuggestionSelect(suggestion);
    };

    const handleSuggestionSelect = (suggestion: AutocompleteSuggestion) => {
        // Update the query with the suggestion name and trigger search
        onQueryChange(suggestion.name);
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        
        // Call the callback if provided, otherwise trigger a form submit
        if (onSuggestionSelect) {
            onSuggestionSelect(suggestion);
        } else {
            // Trigger search with the suggestion name
            setTimeout(() => {
                const form = inputRef.current?.closest('form');
                if (form) {
                    const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                    form.dispatchEvent(submitEvent);
                }
            }, 0);
        }
    };

    const handleCloseSuggestions = () => {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
    };

    const handleClearSearch = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onQueryChange('');
        setSuggestions([]);
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        inputRef.current?.focus();
        
        // Trigger form submission to clear search results
        setTimeout(() => {
            const form = inputRef.current?.closest('form');
            if (form) {
                const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                form.dispatchEvent(submitEvent);
            }
        }, 0);
    };

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    return (
        <div className="search-form-container" ref={containerRef}>
            <form onSubmit={onSubmit} className="search-page-form">
                <label htmlFor="searchPageQuery" className="visually-hidden">
                    Search
                </label>
                <div className="search-input-wrapper">
                    <input
                        ref={inputRef}
                        id="searchPageQuery"
                        className="search-page-form-input"
                        type="text"
                        name="query"
                        placeholder={placeholder}
                        value={query}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        onKeyDown={handleKeyDown}
                        tabIndex={0}
                        autoComplete="off"
                        role="combobox"
                        aria-expanded={showSuggestions}
                        aria-haspopup="listbox"
                        aria-autocomplete="list"
                    />
                    <AutocompleteSuggestions
                        suggestions={suggestions}
                        visible={showSuggestions}
                        onSuggestionClick={handleSuggestionClick}
                        onClose={handleCloseSuggestions}
                        highlightedIndex={highlightedIndex}
                        query={query}
                    />
                </div>
                <svg 
                    className={`svg-icon ${query ? 'svg-icon--clear' : ''}`}
                    onClick={query ? handleClearSearch : undefined}
                    role={query ? 'button' : undefined}
                    aria-label={query ? 'Clear search' : undefined}
                    tabIndex={query ? 0 : -1}
                    onKeyDown={query ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleClearSearch(e as any);
                        }
                    } : undefined}
                >
                    <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref={query ? "#icon-clear" : "#icon-search"} />
                </svg>
                <button
                    id="searchBtn"
                    type="submit"
                    className="btn-primary"
                    tabIndex={0}
                    disabled={loading}
                >
                    {buttonText}
                </button>        
            </form>
        </div>
    );
});

SearchForm.displayName = 'SearchForm';
